import React, { useCallback, useEffect, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CheckCircle2, Circle, RefreshCw, Send, ChevronDown, ChevronUp, Loader2 } from "lucide-react";

function Step({ num, done, title, children }) {
  return (
    <div className="flex gap-3" data-testid={`onboarding-step-${num}`}>
      <div className="flex flex-col items-center">
        {done ? (
          <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" data-testid={`onboarding-step-${num}-done`} />
        ) : (
          <Circle className="h-5 w-5 text-slate-300 shrink-0" />
        )}
        <div className="w-px flex-1 bg-slate-200 my-1" />
      </div>
      <div className="pb-4 flex-1">
        <div className={`text-sm font-medium ${done ? "text-emerald-700" : "text-slate-800"}`}>
          {num}. {title}
        </div>
        <div className="mt-1 text-xs text-slate-500">{children}</div>
      </div>
    </div>
  );
}

export const StrongfitOnboardingWizard = ({ companyId, settings, apiBase }) => {
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(null); // null = auto (offen solange unfertig)
  const [testing, setTesting] = useState(false);

  const load = useCallback(async () => {
    if (!companyId) return;
    try {
      const r = await apiClient.get(`/companies/${companyId}/integration/strongfit/onboarding`);
      setStatus(r.data);
    } catch { /* ignore */ }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const allDone = status && status.api_key_set && status.last_ping_at &&
    status.webhook_configured && status.webhook_test_ok && status.invoice_count > 0;
  const isOpen = open === null ? !allDone : open;

  useEffect(() => {
    if (!isOpen) return undefined;
    const iv = setInterval(load, 8000);
    return () => clearInterval(iv);
  }, [isOpen, load]);

  const sendTestWebhook = async () => {
    setTesting(true);
    try {
      const r = await apiClient.post(`/companies/${companyId}/integration/strongfit/webhooks/test`);
      if (r.data.ok) toast.success("Test-Webhook erfolgreich zugestellt (HTTP 2xx)");
      else toast.error(`Partner-App hat nicht mit 2xx geantwortet (${r.data.status})`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test fehlgeschlagen");
    } finally { setTesting(false); }
  };

  if (!status) return null;
  const doneCount = [status.api_key_set, !!status.last_ping_at, status.webhook_configured,
    status.webhook_test_ok, status.invoice_count > 0].filter(Boolean).length;

  return (
    <div className="mt-4 border border-slate-200 rounded-xl bg-slate-50/60" data-testid="strongfit-onboarding-wizard">
      <button
        className="w-full flex items-center justify-between px-4 py-3 text-left"
        onClick={() => setOpen(!isOpen)}
        data-testid="onboarding-toggle"
      >
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-slate-800">Partner-Onboarding</span>
          <span className={`text-[11px] px-2 py-0.5 rounded-full ${allDone ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
            {allDone ? "abgeschlossen" : `${doneCount}/5 Schritte`}
          </span>
        </div>
        {isOpen ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
      </button>

      {isOpen && (
        <div className="px-4 pb-4">
          <Step num={1} done={status.api_key_set} title="API-Key & Base-URL an die Partner-App übergeben">
            Kopiere unten den <b>API-Key</b> und die <b>Base-URL</b> (ohne /api!) und hinterlege beides in der Partner-App.
          </Step>

          <Step num={2} done={!!status.last_ping_at} title="Verbindungstest (Ping) der Partner-App">
            Die Partner-App ruft <code className="bg-slate-100 px-1 rounded">GET /api/integration/erp/ping</code> mit dem Key auf.
            {status.last_ping_at
              ? <span className="text-emerald-700"> Letzter Ping: {String(status.last_ping_at).slice(0, 16).replace("T", " ")} ✓</span>
              : <span className="text-amber-700"> Noch kein Ping empfangen — Status aktualisiert sich automatisch.</span>}
          </Step>

          <Step num={3} done={status.webhook_configured} title="Webhook-URL der Partner-App speichern">
            Trage unten die Webhook-URL ein und übergib der Partner-App das <b>Webhook-Secret</b> —
            darüber melden wir Zahlungen zurück.
          </Step>

          <Step num={4} done={status.webhook_test_ok} title="Test-Webhook senden">
            Prüft, ob die Partner-App unsere Zahlungs-Signale empfängt.
            {status.webhook_test_status && !status.webhook_test_ok && (
              <span className="text-red-600"> Letzter Versuch: {status.webhook_test_status}</span>
            )}
            <div className="mt-1.5">
              <Button size="sm" variant="outline" className="h-7 text-xs" disabled={testing || !status.webhook_configured}
                onClick={sendTestWebhook} data-testid="onboarding-test-webhook-button">
                {testing ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Send className="h-3 w-3 mr-1" />}
                Test-Webhook senden
              </Button>
            </div>
          </Step>

          <Step num={5} done={status.invoice_count > 0} title="Erste Test-Rechnung empfangen">
            Die Partner-App sendet eine Rechnung an <code className="bg-slate-100 px-1 rounded">POST /api/integration/erp/invoices</code>.
            {status.invoice_count > 0 ? (
              <span className="text-emerald-700">
                {" "}{status.invoice_count} Rechnung(en) empfangen — zuletzt {status.last_invoice?.invoice_number}
                {" "}({status.last_invoice?.customer_name || "—"}, CHF {Number(status.last_invoice?.total || 0).toFixed(2)}
                {status.last_invoice?.qr_reference ? ", QR-Referenz ✓" : ", ⚠️ ohne QR-Referenz"}) ✓
              </span>
            ) : (
              <span className="text-amber-700"> Noch keine Rechnung empfangen.</span>
            )}
          </Step>

          <div className="flex items-center justify-between mt-1">
            <p className="text-[11px] text-slate-400">
              Status aktualisiert sich alle 8 Sekunden automatisch.
            </p>
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={load} data-testid="onboarding-refresh">
              <RefreshCw className="h-3 w-3 mr-1" /> Aktualisieren
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default StrongfitOnboardingWizard;
